import type { Ref, RefCallback } from 'react';
import React from 'react';
import type { Component, FormioFormProps } from '@formio/react';
import { ReactComponent } from '@formio/react';
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import { intl } from '../../../lib';
import type { LanguageProperty } from '../../generated/LanguageProperty';

/**
 * The data that is used to initialize the formio component.
 * IMPORTANT: the type has to match the name under which you register your component.
 */
export type FormioBuilderSchema = {
  type: string;
  label: string;
  key: string;
} & Record<string, unknown>;

/**
 * The information that is displayed in the FormBuilder.
 */
export type FormioBuilderInfo = {
  title: string;
  group: string;
  icon: string;
  documentation: string;
  weight: number;
  schema: Record<string, unknown>;
};
export type EhsFormioReactProps<T> = {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly label: string;
  readonly settings: T;
  readonly readOnly: boolean;
  readonly required: boolean;
  readonly defaultValue?: any;
  readonly keyForComponent?: string;
};

/**
 * A wrapper to make the usage of react in formio easier.
 *
 * IMPORTANT!!!!!
 * Do not forget to add the two static methods schema and builderInfo.
 *
 * Builderinfo is used to display the component in the FormBuilder and schema is then used to initialize it.
 * The type field is especially important since it is used to retrieve your registered component.
 *
 * @param T Settings that will be passed to react component
 * @param H fields that are available in the constructor
 */
export abstract class EhsFormioReactComponent<T, H> extends ReactComponent<H> {
  private _reactRoot: Root | undefined = undefined;
  private ehsLabel = '';
  private component: Component<any>;
  private readOnly: boolean;
  private required = false;
  private defaultComponentValue?: any; // cannot make defaultValue, since that have only getter.
  private keyForComponent?: string;
  private validators: string[] = [];

  /**
   * if noField is set to true, the additional label is not rendered
   * https://github.com/formio/formio.js/blob/master/src/components/_classes/field/Field.js
   */
  private noField = true;

  constructor(
    component: Component<H>,
    options: FormioFormProps['options'],
    data: Record<string, unknown>
  ) {
    super(component, options, data);
    // set the translated label here, or if it is not a language key, fall back to the passed value
    this.ehsLabel = intl(component.label as LanguageProperty, component.label);
    this.component = component;
    this.readOnly = (options?.readOnly || component.disabled) ?? false;

    this.defaultComponentValue =
      component.defaultValue ?? data ? data[component.key ?? ''] : '';

    this.required = component?.validate?.required ?? false;
    this.keyForComponent = component.id;
    this.validators = component.validators ?? [];
  }

  abstract extractFields(component: Component<H>): void;
  /**
   * Attaches the {@link FormioReactWrapper} that forwards value and onChange
   *
   * @param element the html element were we attach react to
   * @param ref a ref to {@link FormioReactWrapper} that handles state changes
   */
  attachReact(element: HTMLElement, ref: (val: unknown) => void): void {
    const updateValue = this.updateValue.bind(this);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    const onChange = function (val: string) {
      updateValue(val, { modified: true });
      // also update `dataForSetting` with the correct value
      that.dataForSetting = val;
    }.bind(this);
    const label = this.ehsLabel;
    this.extractFields(this.component);
    if (this._reactRoot == null) {
      this._reactRoot = createRoot(element);
    }
    const [child, settings] = this.attachEhsReact();
    this._reactRoot.render(
      <FormioReactWrapper
        ref={ref}
        onChange={onChange}
        child={child}
        settings={settings}
        label={label}
        readOnly={this.readOnly}
        required={this.required}
        defaultValue={this.defaultComponentValue}
        keyForComponent={this.keyForComponent}
      />
    );
  }

  /**
   * Callback ref to store a reference to the node.
   *
   * @param element - the node
   */
  setReactInstance(element: HTMLElement): void {
    super.setReactInstance(element);
    // notify formio that the value of the element should be set
    this.shouldSetValue = true;
    if (element != null) {
      this.lastSetValueFn?.();
    }
  }

  private lastSetValueFn?: () => void;

  setValue(value: string): void {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    if (this['reactInstance'] != null) {
      super.setValue(value);
    } else {
      // add setting back to macro task queue, so react can finish setting the reference
      const setValueRef = super.setValue.bind(this);
      this.lastSetValueFn = () => setValueRef(value);
    }
    // always set the `dataForSetting`, even when it's not needed
    // to ensure that `setReactInstance` can trigger `shouldSetValue` with the correct value
    this.dataForSetting = value;
  }

  /**
   * At the end of the lifetime detach react again.
   *
   * @param element the html element were we attached react to
   */
  detachReact(_element: HTMLElement): void {
    const root = this._reactRoot;
    this._reactRoot = undefined;
    setTimeout(() => root?.unmount(), 0);
  }

  /**
   * Return your React component here that adheres to the interface.
   */
  abstract attachEhsReact(): [
    (props: EhsFormioReactProps<T>) => React.JSX.Element,
    T
  ];
}
type FormioReactWrapperProps<T> = {
  ref?: RefCallback<unknown>;
  onChange: (value: string) => void;
  child: (props: EhsFormioReactProps<T>) => React.JSX.Element;
  label: string;
  settings: T;
  readOnly: boolean;
  required: boolean;
  defaultValue?: any;
  keyForComponent?: string;
};

/**
 * A React class component that is used as a wrapper so we can use components normally and typesafe.
 *
 * It needs to have a state value so that the {@link EhsFormioReactComponent} can retrieve and set the value.
 */
class FormioReactWrapper<T> extends React.Component<
  FormioReactWrapperProps<T>,
  { value: string }
> {
  state = {
    value: ''
  };

  constructor(props: FormioReactWrapperProps<T>) {
    super(props);
    this.onChange = this.onChange.bind(this);
  }
  onChange(value: string): void {
    this.setState(prev => ({ ...prev, value: value }));
    //forward the state change to the formio component
    this.props.onChange(value);
  }
  componentDidMount() {
    this.props.ref?.(this);
  }
  render() {
    return (
      <div>
        <this.props.child
          value={this.state.value}
          onChange={this.onChange}
          label={this.props.label}
          settings={this.props.settings}
          readOnly={this.props.readOnly}
          required={this.props.required}
          defaultValue={this.props.defaultValue}
          keyForComponent={this.props.keyForComponent}
        />
      </div>
    );
  }
}
